import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AthleteConfigSheet from './AthleteConfigSheet'
import AccountDetailsSheet from './AccountDetailsSheet'
import HealthLegalSheet from './HealthLegalSheet'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import StatusPill from '../ui/StatusPill'
import { LegalInfoModal } from '../compliance/Disclaimer'
import { PrivacySettingsSheet } from '../compliance/PrivacySettingsSheet'
import { healthScreeningQuestions } from '../../lib/i18nCatalog'
import { KILO } from '../../constants/KILO_THEME'
import { MATERIAL_ICONS } from '../../lib/materialIcons'
import { useUserLanguage } from '../../hooks/useUserLanguage'
import { logWeighIn, updateBodyMetrics, updateCycleTracking, updateProfilePreferences } from '../../lib/profileMetrics'
import type { ProfileStats } from '../../lib/profileStats'
import { isProPlusTier, isProTier, planLabelKey, type UserTierState } from '../../lib/tier'
import { redirectToBillingPortal, syncStripeBilling } from '../../lib/stripeBilling'
import { captureError } from '../../lib/sentry'
import { showToast } from '../../lib/toast'
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushPermissionState,
  isPushConfigured,
  isPushSupported,
  type PushPermissionState,
} from '../../lib/pushNotifications'
import ConflictListSheet from '../calendar/ConflictListSheet'
import { dismissConflict, loadPendingConflictAlerts, rescheduleConflict, type ConflictAlert, type SuggestedSlot } from '../../lib/conflicts'
import { supabase } from '../../lib/supabaseClient'

type ProfileDashboardProps = {
  userId: string | null
  firstName: string
  tier: UserTierState | null
  stats: ProfileStats | null
  onSignOut: () => void
  onUpgrade: () => void
  onSyncCalendar: () => void
  calendarSyncing?: boolean
  onMetricsUpdated?: () => void
  onBillingUpdated?: () => void
  onWellnessDataDeleted?: () => void
}

const ProfileDashboard = ({
  userId,
  firstName,
  tier,
  stats,
  onSignOut,
  onUpgrade,
  onSyncCalendar,
  calendarSyncing = false,
  onMetricsUpdated,
  onBillingUpdated,
  onWellnessDataDeleted,
}: ProfileDashboardProps) => {
  const { t } = useTranslation()
  const { language, changeLanguage, error: languageError } = useUserLanguage(userId)
  const isPro = isProTier(tier)
  const isProPlus = isProPlusTier(tier)
  const hasStripeBilling = Boolean(tier?.stripe_customer_id)
  const showRestoreBilling = hasStripeBilling && !isPro && !isProPlus

  const openBillingPortal = async () => {
    setBillingLoading(true)
    setBillingError('')
    try {
      await redirectToBillingPortal()
    } catch (error) {
      setBillingError(error instanceof Error ? error.message : t('profile.billingPortalError'))
      setBillingLoading(false)
    }
  }
  const [metricError, setMetricError] = useState('')
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false)
  const [showDataPrivacy, setShowDataPrivacy] = useState(false)
  const [showHealthScreening, setShowHealthScreening] = useState(false)
  const [billingLoading, setBillingLoading] = useState(false)
  const [billingError, setBillingError] = useState('')
  const [restoreLoading, setRestoreLoading] = useState(false)
  const [screeningExclusions, setScreeningExclusions] = useState<string[]>([])
  const [pushState, setPushState] = useState<PushPermissionState>('unsupported')
  const [pushLoading, setPushLoading] = useState(false)
  const [pushError, setPushError] = useState('')
  const [conflictsOpen, setConflictsOpen] = useState(false)
  const [conflicts, setConflicts] = useState<ConflictAlert[]>([])
  const [conflictsLoading, setConflictsLoading] = useState(false)
  const [athleteConfigOpen, setAthleteConfigOpen] = useState(false)
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false)
  const [healthLegalOpen, setHealthLegalOpen] = useState(false)

  const restoreBilling = async () => {
    setRestoreLoading(true)
    setBillingError('')
    try {
      await syncStripeBilling()
      onBillingUpdated?.()
      onMetricsUpdated?.()
    } catch (error) {
      captureError(error, { action: 'restore_subscription' })
      showToast({
        type: 'info',
        message: t('profile.billingRestoreNoneFound'),
        action: { label: t('profile.upgradePrice'), onClick: onUpgrade },
        dismissable: true,
        duration: 6000,
      })
    } finally {
      setRestoreLoading(false)
    }
  }

  useEffect(() => {
    if (!userId) return
    void getPushPermissionState().then(setPushState)
  }, [userId])

  const openConflicts = async () => {
    if (!userId) return
    setConflictsLoading(true)
    const { alerts, error } = await loadPendingConflictAlerts(userId)
    setConflictsLoading(false)
    if (error) {
      setMetricError(error)
      return
    }
    setConflicts(alerts)
    setConflictsOpen(true)
  }

  const handleProfileReschedule = async (alert: ConflictAlert, slot: SuggestedSlot) => {
    if (!alert.kilo_block_id) return
    const { error } = await rescheduleConflict(alert.id, alert.kilo_block_id, slot, alert.occurrence_date)
    if (error) {
      setMetricError(error)
      return
    }
    await openConflicts()
    onMetricsUpdated?.()
  }

  const handleProfileDismiss = async (alertId: string) => {
    const { error } = await dismissConflict(alertId)
    if (error) {
      setMetricError(error)
      return
    }
    await openConflicts()
    onMetricsUpdated?.()
  }

  useEffect(() => {
    if (!userId) return
    void supabase
      .from('profiles')
      .select('health_screening_exclusions, disclaimer_accepted_at')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.health_screening_exclusions) {
          setScreeningExclusions(data.health_screening_exclusions as string[])
        }
      })
  }, [userId])

  const togglePushNotifications = async () => {
    setPushLoading(true)
    setPushError('')
    try {
      if (pushState === 'subscribed') {
        const result = await disablePushNotifications()
        if (!result.ok) setPushError(result.error ?? t('profile.pushDisableError'))
        else setPushState('default')
      } else {
        const result = await enablePushNotifications()
        if (!result.ok) setPushError(result.error ?? t('profile.pushEnableError'))
        else setPushState('subscribed')
      }
    } finally {
      setPushLoading(false)
    }
  }

  const pushSupported = isPushSupported()
  const pushConfigured = isPushConfigured()
  const pushReady = pushSupported && pushConfigured

  const pushDescription = !pushConfigured
    ? t('profile.pushNotConfiguredDescription')
    : !pushSupported
      ? t('profile.pushUnsupportedDescription')
      : pushState === 'subscribed'
        ? t('profile.pushEnabledDescription')
        : pushState === 'denied'
          ? t('profile.pushDeniedDescription')
          : t('profile.pushDisabledDescription')

  const saveHeight = async (value: number) => {
    if (!userId) return
    const { error } = await updateBodyMetrics(userId, { height_cm: value })
    if (error) setMetricError(error)
    else onMetricsUpdated?.()
  }

  const saveWeight = async (value: number) => {
    if (!userId) return
    const { error } = await logWeighIn(userId, value)
    if (error) setMetricError(error)
    else onMetricsUpdated?.()
  }

  const saveCycle = async (fields: {
    last_period_start_date: string | null
    avg_cycle_length: number
    avg_period_duration: number
  }) => {
    if (!userId) return
    const { error } = await updateCycleTracking(userId, fields)
    if (error) setMetricError(error)
    else onMetricsUpdated?.()
  }

  const cycleProfile = stats
    ? {
        sex: stats.sex,
        last_period_start_date: stats.last_period_start_date,
        avg_cycle_length: stats.avg_cycle_length,
        avg_period_duration: stats.avg_period_duration,
      }
    : null

  return (
    <div className={`flex flex-col gap-3 pb-24 ${KILO.bg.page}`}>
      <header className={KILO.stickyHeader}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={KILO.meta}>{t('profile.athleteProfile')}</p>
            <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{t('profile.greeting', { name: firstName || t('common.athlete') })}</h1>
            <p className={`mt-1 text-sm ${KILO.text.soft}`}>{t('profile.subtitle')}</p>
          </div>
          <button
            type="button"
            aria-label={t('profile.account.openDetails')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-kilo-line bg-kilo-nested transition-colors hover:border-kilo-accent/40 hover:bg-kilo-accent/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kilo-accent"
            onClick={() => setAccountDetailsOpen(true)}
          >
            <MaterialIcon name="person" filled size="lg" />
          </button>
        </div>
      </header>

      {metricError || languageError ? <p className="text-sm text-red-400">{metricError || languageError}</p> : null}

      {stats ? (
        <button
          type="button"
          className={`${KILO.bg.card} ${KILO.border.default} ${KILO.radius.card} w-full p-4 text-left transition-colors hover:border-kilo-accent/30`}
          onClick={() => setAthleteConfigOpen(true)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <MaterialIcon name="health_metrics" hoverable size="sm" />
                <p className={KILO.meta}>{t('profile.athleteConfig')}</p>
                {stats.pending_conflicts > 0 ? (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 font-mono text-[10px] font-bold text-black">
                    {stats.pending_conflicts}
                  </span>
                ) : null}
              </div>
            </div>
            <MaterialIcon name="chevron_right" size="sm" className="shrink-0" />
          </div>
        </button>
      ) : null}

      <BentoCard gradient="none">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={KILO.meta}>{t('profile.subscriptionTier')}</p>
            <p className={`mt-1 text-xl font-bold tracking-tight ${KILO.text.primary}`}>
              {t(planLabelKey(tier?.plan ?? 'free'))}
            </p>
            {tier?.trial_end_date && (isPro || isProPlus) ? (
              <p className={`mt-1 text-xs ${KILO.text.muted}`}>{t('profile.trialEnds', { date: tier.trial_end_date })}</p>
            ) : null}
          </div>
          <StatusPill
            label={isProPlus ? t('app.profile.statusProPlus') : isPro ? t('app.profile.statusPro') : t('app.profile.statusFree')}
            active={isPro || isProPlus}
          />
        </div>

        {!isPro && !isProPlus ? (
          <>
            <button type="button" className="group mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-bold text-black" onClick={onUpgrade}>
              <MaterialIcon name="workspace_premium" hoverable size="sm" />
              {t('profile.upgradePrice')}
            </button>
            {showRestoreBilling ? (
              <button
                type="button"
                disabled={restoreLoading}
                className={`group/btn mt-3 ${KILO.btnSecondary}`}
                onClick={() => void restoreBilling()}
              >
                <MaterialIcon name="sync" hoverable size="sm" />
                {restoreLoading ? t('profile.restoringBilling') : t('profile.restoreBilling')}
              </button>
            ) : null}
          </>
        ) : hasStripeBilling && (isPro || isProPlus) ? (
          <button
            type="button"
            disabled={billingLoading}
            className={`group/btn mt-4 ${KILO.btnSecondary}`}
            onClick={() => void openBillingPortal()}
          >
            <MaterialIcon name="credit_card" hoverable size="sm" />
            {billingLoading ? t('profile.openingBillingPortal') : t('profile.manageSubscription')}
          </button>
        ) : null}
        {billingError ? <p className="mt-2 text-xs text-red-400">{billingError}</p> : null}
      </BentoCard>

      {isProPlus ? (
        <BentoCard gradient="none" className="group">
          <div className="flex items-center gap-2">
            <MaterialIcon name="calendar_add_on" filled={Boolean(tier?.calendar_sync_enabled)} hoverable={!tier?.calendar_sync_enabled} size="sm" />
            <p className={KILO.meta}>{t('profile.calendarIntegration')}</p>
          </div>
          <p className={`mt-2 text-sm ${KILO.text.muted}`}>
            {tier?.calendar_sync_enabled ? t('profile.calendarConnected') : t('profile.calendarDisconnected')}
          </p>
          <button type="button" disabled={calendarSyncing} className={`group/btn mt-4 ${KILO.btnSecondary}`} onClick={onSyncCalendar}>
            <MaterialIcon name="calendar_add_on" hoverable size="sm" />
            {calendarSyncing
              ? t('calendar.syncing')
              : tier?.calendar_sync_enabled
                ? t('profile.resyncCalendar')
                : t('profile.connectGoogleCalendar')}
          </button>
        </BentoCard>
      ) : null}

      {isProPlus ? (
        <BentoCard gradient="none">
          <div className="flex items-center gap-2">
            <MaterialIcon name="notifications" filled={pushState === 'subscribed'} hoverable={pushState !== 'subscribed'} size="sm" />
            <p className={KILO.meta}>{t('profile.pushNotifications')}</p>
          </div>
          <p className={`mt-2 text-sm ${KILO.text.muted}`}>{pushDescription}</p>
          <button
            type="button"
            disabled={pushLoading || !pushReady || pushState === 'denied' || pushState === 'unsupported'}
            className={`group/btn mt-4 ${KILO.btnSecondary}`}
            onClick={() => void togglePushNotifications()}
          >
            <MaterialIcon name="notifications_active" hoverable size="sm" />
            {pushLoading
              ? t('profile.pushWorking')
              : pushState === 'subscribed'
                ? t('profile.disablePushNotifications')
                : t('profile.enablePushNotifications')}
          </button>
          {pushError ? <p className="mt-2 text-xs text-red-400">{pushError}</p> : null}
        </BentoCard>
      ) : null}

      <BentoCard gradient="none">
        <div className="flex items-center gap-2">
          <MaterialIcon name={MATERIAL_ICONS.watchScreentime} hoverable size="sm" />
          <p className={KILO.meta}>{t('profile.wearables')}</p>
        </div>
        <p className={`mt-2 text-sm ${KILO.text.muted}`}>{t('profile.wearablesNote')}</p>
      </BentoCard>

      <button
        type="button"
        className={`${KILO.bg.card} ${KILO.border.default} ${KILO.radius.card} w-full p-4 text-left transition-colors hover:border-kilo-accent/30`}
        onClick={() => setHealthLegalOpen(true)}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <MaterialIcon name="info" hoverable size="sm" />
              <p className={KILO.meta}>{t('profile.healthLegal')}</p>
            </div>
            {screeningExclusions.length > 0 ? (
              <p className="mt-2 text-xs text-amber-400">{t('profile.screeningReview')}</p>
            ) : null}
          </div>
          <MaterialIcon name="chevron_right" size="sm" className="shrink-0" />
        </div>
      </button>

      {stats ? (
        <AthleteConfigSheet
          open={athleteConfigOpen}
          onClose={() => setAthleteConfigOpen(false)}
          stats={stats}
          cycleProfile={cycleProfile}
          language={language}
          onLanguageChange={(next) => void changeLanguage(next)}
          conflictsLoading={conflictsLoading}
          onOpenConflicts={() => {
            setAthleteConfigOpen(false)
            void openConflicts()
          }}
          onSaveHeight={saveHeight}
          onSaveWeight={saveWeight}
          onSaveTrainingGoal={async (value) => {
            if (!userId) return
            const { error } = await updateProfilePreferences(userId, { weekly_commitment: value })
            if (error) setMetricError(error)
            else onMetricsUpdated?.()
          }}
          onSaveMealFrequency={async (value) => {
            if (!userId) return
            const meal_frequency = value >= 5 ? '5_plus_meals' : value === 4 ? '4_meals' : '3_meals'
            const { error } = await updateProfilePreferences(userId, { meal_frequency })
            if (error) setMetricError(error)
            else onMetricsUpdated?.()
          }}
          onSaveBudget={async (value) => {
            if (!userId) return
            const { error } = await updateProfilePreferences(userId, { weekly_budget_value: value })
            if (error) setMetricError(error)
            else onMetricsUpdated?.()
          }}
          onSaveTrainingTime={async (value) => {
            if (!userId) return
            const { error } = await updateProfilePreferences(userId, { preferred_workout_start: value })
            if (error) setMetricError(error)
            else onMetricsUpdated?.()
          }}
          onSaveCycle={saveCycle}
        />
      ) : null}

      <AccountDetailsSheet
        open={accountDetailsOpen}
        onClose={() => setAccountDetailsOpen(false)}
        userId={userId}
        fallbackName={firstName}
      />

      <HealthLegalSheet
        open={healthLegalOpen}
        onClose={() => setHealthLegalOpen(false)}
        screeningExclusions={screeningExclusions}
        onOpenDisclaimer={() => {
          setHealthLegalOpen(false)
          setShowFullDisclaimer(true)
        }}
        onOpenPrivacy={() => {
          setHealthLegalOpen(false)
          setShowDataPrivacy(true)
        }}
        onOpenScreening={() => {
          setHealthLegalOpen(false)
          setShowHealthScreening(true)
        }}
      />

      {showFullDisclaimer ? (
        <LegalInfoModal
          title={t('profile.wellnessDisclaimer')}
          body={t('compliance.fullDisclaimer')}
          onClose={() => setShowFullDisclaimer(false)}
        />
      ) : null}

      {showDataPrivacy ? (
        <PrivacySettingsSheet
          onClose={() => setShowDataPrivacy(false)}
          onAccountDeleted={onSignOut}
          onWellnessDataDeleted={() => {
            setShowDataPrivacy(false)
            onWellnessDataDeleted?.()
          }}
        />
      ) : null}

      {showHealthScreening ? (
        <LegalInfoModal
          title={t('profile.healthScreening')}
          body={
            screeningExclusions.length === 0
              ? t('profile.screeningNone')
              : t('profile.screeningFlagged', {
                  items: screeningExclusions
                    .map((id) => healthScreeningQuestions(t).find((q) => q.id === id)?.question ?? id)
                    .join(' · '),
                })
          }
          onClose={() => setShowHealthScreening(false)}
        />
      ) : null}

      <ConflictListSheet
        open={conflictsOpen}
        onClose={() => setConflictsOpen(false)}
        conflicts={conflicts}
        onReschedule={handleProfileReschedule}
        onDismiss={handleProfileDismiss}
      />

      <button type="button" className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-alt text-sm font-semibold text-kilo-soft transition-colors hover:border-kilo-accent/40 hover:text-kilo-text" onClick={onSignOut}>
        <MaterialIcon name="logout" hoverable size="sm" />
        {t('profile.signOut')}
      </button>
    </div>
  )
}

export default ProfileDashboard
